import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";

const COLORS = ["#22c55e", "#06b6d4", "#8b5cf6", "#ec4899", "#f59e0b", "#ef4444", "#14b8a6"];

export default function EmojiDaysActivityGraph({ history = [] }) {
  const data = useMemo(() => {
    const order = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const stats = { Sun: 0, Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0 };

    history.forEach((item) => {
      const dayName = new Date(item.confirmedAt || item.createdAt).toLocaleDateString("en-US", { weekday: "short" });
      if (stats[dayName] !== undefined) stats[dayName]++;
    });

    return order.map((day) => ({
      day,
      hunts: stats[day],
    }));
  }, [history]);

  const totalHunts = data.reduce((sum, item) => sum + item.hunts, 0);
  const maxHunts = Math.max(...data.map((d) => d.hunts));

  return (
    <div className="rounded-2xl border border-cyan-500/20 bg-slate-900 p-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="font-black text-cyan-50 text-lg">
            📊 Weekly Activity
          </div>
          <div className="text-xs text-slate-100">
            Your Emoji Hunting Consistency
          </div>
        </div>

        <div className="text-right">
          <div className="text-2xl font-black text-yellow-400">
            {totalHunts}
          </div>
          <div className="text-xs text-slate-200 -mt-1">Total Hunts</div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} barCategoryGap={12}>
          <XAxis
            dataKey="day"
            tick={{ fill: "#f3f4f6", fontSize: 12 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fill: "#94a3b8", fontSize: 12 }}
            axisLine={false}
            tickLine={false}
          />

          <Tooltip
            labelStyle={{ color: "#f3f4f6" }}
            cursor={{ fill: "rgba(255,255,255,0.08)" }}
            contentStyle={{
              backgroundColor: "#0f172a",
              border: "1px solid #22d3ee",
              borderRadius: "12px",
              color: "#fff",
            }}
            formatter={(value) => [
              <span style={{ color: "#f3f4f6" }}>{value} Hunts</span>,
              <span style={{ color: "#f3f4f6" }}>Activity</span>,
            ]}
          />

          <Bar
            dataKey="hunts"
            radius={[6, 6, 0, 0]}
            activeBar={{ fillOpacity: 0.9, stroke: "#f8fafc", strokeWidth: 2 }}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={
                  entry.hunts === maxHunts && maxHunts > 0
                    ? "#facc15"
                    : COLORS[index % COLORS.length]
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Day Summary */}
      <div className="grid grid-cols-7 gap-2 mt-4">
        {data.map((item, index) => (
          <div key={index} className="text-center">
            <div className="text-xl mb-1">
              {item.hunts === 0
                ? "😴"
                : item.hunts < 2
                  ? "🙂"
                  : item.hunts < 4
                    ? "🔥"
                    : "🏆"}
            </div>
            <div className="text-xs text-slate-100">{item.day}</div>
            <div className="font-bold text-cyan-300 text-sm">{item.hunts}</div>
          </div>
        ))}
      </div>
    </div>
  );
}